import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { VehicleDetailProps, VehicleDetailRef } from "./types";
import { useTranslate } from "helpers";
import { Grid } from "ui/components/Grid";
import { AvailableWidget, GridRef } from "ui/components/Grid/types";
import { generateGridFromTemplate } from "ui/components/Grid/helper";
import { GridTemplate, GRID_TEMPLATES } from "ui/components/Grid/templates";
import WidgetEditCard from "./components/WidgetEditCard/WidgetEditCard";
import {
  FLEET_VEHICLE_AVAILABLE_WIDGETS,
  VEHICLE_CONTRACT_WIDGET_ID,
  VEHICLE_INVOICE_WIDGET_ID,
  VEHICLE_OFFER_WIDGET_ID,
} from "./components/WidgetEditCard/consts";
import { VehicleDriverRef } from "./components/VehicleDriverCard/types";
import { VehicleDocumentRef } from "./components/VehicleDocumentCard/types";
import { VehicleExpenseRef } from "./components/VehicleExpenseCard/types";
import { VehicleOfferRef } from "./components/VehicleOfferCard/types";
import { VehicleContractRef } from "./components/VehicleContractCard/types";
import { VehicleInvoiceRef } from "./components/VehicleInvoiceCard/types";
import { VehicleGalleryRef } from "./components/VehicleGalleryCard/types";
import { VehicleInfoRef } from "./components/VehicleInfoCard/types";

const VehicleDetailCard = forwardRef<VehicleDetailRef, VehicleDetailProps>((props, ref) => {
  const { fleetVehicleDetail, showAddWidgetModal, setShowAddWidgetModal, onVehicleRefresh, onUserWidgetsChange } =
    props;

  const { t } = useTranslate();
  const gridRef = useRef<GridRef>(null);

  const galleryRef = useRef<VehicleGalleryRef>(null);
  const infoRef = useRef<VehicleInfoRef>(null);
  const driverRef = useRef<VehicleDriverRef>(null);
  const documentRef = useRef<VehicleDocumentRef>(null);
  const expenseRef = useRef<VehicleExpenseRef>(null);
  const offerRef = useRef<VehicleOfferRef>(null);
  const contractRef = useRef<VehicleContractRef>(null);
  const invoiceRef = useRef<VehicleInvoiceRef>(null);

  //1. pokušaj dohvatiti spremljeni state
  const savedState = JSON.parse(
    localStorage.getItem("fleet-vehicle-state") || "null"
  );

  //2. inicijalizacija
  const initialUserWidgets =
    savedState?.userWidgets ??
    FLEET_VEHICLE_AVAILABLE_WIDGETS(fleetVehicleDetail, galleryRef, infoRef, driverRef, documentRef, expenseRef, offerRef, contractRef, invoiceRef, t, onVehicleRefresh).map((w) => w.id);
  const initialLayouts = savedState?.layouts ?? null;

  const [userWidgets, setUserWidgets] = useState<string[]>(initialUserWidgets);
  const [template, setTemplate] = useState<GridTemplate>(
    savedState?.template ?? "three-columns"
  );

  //3. generiraj grid
  const templateConfig = useMemo(
    () => GRID_TEMPLATES.find((tpl) => tpl.id === template) || GRID_TEMPLATES[0],
    [template]
  );

  const { widgets, layouts, widgetIds } = useMemo(() => {
    const availableWidgets = userWidgets
      .map((id) =>
        FLEET_VEHICLE_AVAILABLE_WIDGETS(fleetVehicleDetail, galleryRef, infoRef, driverRef, documentRef, expenseRef, offerRef, contractRef, invoiceRef, t, onVehicleRefresh).find((w) => w.id === id)
      )
      .filter(Boolean) as AvailableWidget[];

    return {
      ...generateGridFromTemplate(templateConfig, availableWidgets),
      widgetIds: availableWidgets.map((w) => w.id),
    };
  }, [userWidgets, t, templateConfig]);

  // Widget se montira tek kad uđe u viewport, pa ga akcija iz izbornika mora prvo montirati —
  // inače je ref widgeta ispod ekrana null i klik ne napravi ništa. Grid widgete zna po
  // poziciji ("widget-0", "widget-1"…, vidi generateGridFromTemplate), ne po njihovom id-u.
  const openInWidget = (widgetId: string, action: () => void) => {
    const index = widgetIds.indexOf(widgetId);
    if (index === -1) return;

    gridRef.current?.revealWidget(`widget-${index}`, action);
  };

  useImperativeHandle(ref, () => ({
    openAddNewOfferSale: () => openInWidget(VEHICLE_OFFER_WIDGET_ID, () => offerRef.current?.openAddNewSale()),
    openAddNewContractSale: () => openInWidget(VEHICLE_CONTRACT_WIDGET_ID, () => contractRef.current?.openAddNewSale()),
    openAddNewInvoiceSale: () => openInWidget(VEHICLE_INVOICE_WIDGET_ID, () => invoiceRef.current?.openAddNewSale()),
    openAddNewOfferRent: () => openInWidget(VEHICLE_OFFER_WIDGET_ID, () => offerRef.current?.openAddNewRent()),
    openAddNewContractRent: () => openInWidget(VEHICLE_CONTRACT_WIDGET_ID, () => contractRef.current?.openAddNewRent()),
    openAddNewInvoiceRent: () => openInWidget(VEHICLE_INVOICE_WIDGET_ID, () => invoiceRef.current?.openAddNewRent()),
  }));

  //4. sinkronizacija (widgets + layouts + template)
  const saveDashboardState = useCallback(
    (layoutsToSave?: any) => {
      localStorage.setItem(
        "fleet-vehicle-state",
        JSON.stringify({
          userWidgets,
          template,
          layouts: layoutsToSave ?? savedState?.layouts ?? layouts,
        })
      );
    },
    [userWidgets, template, layouts, savedState?.layouts]
  );

  useEffect(() => {
    saveDashboardState();
  }, [userWidgets, template, saveDashboardState]);

  useEffect(() => {
    onUserWidgetsChange?.(userWidgets);
  }, [userWidgets, onUserWidgetsChange]);

  const handleTemplateChange = useCallback(
    (newTemplate: GridTemplate) => {
      setTemplate(newTemplate);
      // Clear saved layouts so the new template generates fresh ones
      localStorage.setItem(
        "fleet-vehicle-state",
        JSON.stringify({
          userWidgets,
          template: newTemplate,
          layouts: null,
        })
      );
    },
    [userWidgets]
  );

  const handleEditWidgetSuccess = useCallback(() => {
    setShowAddWidgetModal(false);
  }, []);

  return (
    <>
      <div className="min-h-screen">
        <Grid
          key={`${template}-${userWidgets.join(",")}`}
          ref={gridRef}
          rowHeight={50}
          initialWidgets={widgets}
          initialLayouts={initialLayouts ?? layouts}
          onLayoutChange={(newLayouts) => saveDashboardState(newLayouts)}
        />
      </div>
      <WidgetEditCard
        availableWidgets={FLEET_VEHICLE_AVAILABLE_WIDGETS(fleetVehicleDetail, galleryRef, infoRef, driverRef, documentRef, expenseRef, offerRef, contractRef, invoiceRef, t, onVehicleRefresh)}
        userWidgets={userWidgets}
        setUserWidgets={setUserWidgets}
        template={template}
        onTemplateChange={handleTemplateChange}
        show={showAddWidgetModal}
        onSuccess={handleEditWidgetSuccess}
        onClose={() => setShowAddWidgetModal(false)}
      />
    </>
  );
});
export default memo(VehicleDetailCard);
